// Utility functions for date formatting

// Parse date from various formats and return a valid Date object.
//
// This runs on every row render and twice per comparison while sorting, so on a
// list of several thousand appointments it is called hundreds of thousands of
// times per render. It must stay allocation-light and silent - the debug
// logging that used to be here was enough on its own to lock up the tab and
// leave rows looking blank.
export const parseDate = (dateString) => {
  if (!dateString) {
    return null;
  }

  // If it's already a Date object, return it
  if (dateString instanceof Date) {
    return dateString;
  }

  // Handle Firestore Timestamp objects with toDate method
  if (typeof dateString === 'object' && dateString.toDate) {
    return dateString.toDate();
  }

  // Handle Firestore Timestamp objects with _seconds and _nanoseconds
  if (typeof dateString === 'object' && dateString._seconds !== undefined) {
    const seconds = dateString._seconds;
    const nanoseconds = dateString._nanoseconds || 0;
    const milliseconds = seconds * 1000 + nanoseconds / 1000000;
    return new Date(milliseconds);
  }

  // Handle the non-underscored Firestore Timestamp shape as well
  if (typeof dateString === 'object' && dateString.seconds !== undefined) {
    const nanoseconds = dateString.nanoseconds || 0;
    return new Date(dateString.seconds * 1000 + nanoseconds / 1000000);
  }

  // If it's a timestamp (number), convert to Date
  if (typeof dateString === 'number') {
    return new Date(dateString);
  }

  // Try to parse as ISO string
  if (typeof dateString === 'string') {
    // Handle ISO date strings
    if (dateString.includes('T') || dateString.includes('Z')) {
      const parsed = new Date(dateString);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    // Handle date strings without time
    if (dateString.includes('-')) {
      const parsed = new Date(dateString + 'T00:00:00');
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    // Handle other date formats
    const parsed = new Date(dateString);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return null;
};

/**
 * Egypt writes dates day-first (21/12/2026). 'en-GB' is the English locale that
 * produces day/month/year, so it matches local convention while keeping the
 * month names in English.
 *
 * Kept separate from the US default so switching one part of the app over does
 * not silently reformat dates everywhere else.
 */
export const EG_LOCALE = 'en-GB';
const US_LOCALE = 'en-US';

// Format date for display
export const formatDate = (dateString, options = {}, locale = US_LOCALE) => {
  // Handle null, undefined, or empty string
  if (!dateString || dateString === '') {
    return 'Invalid Date';
  }

  const date = parseDate(dateString);

  if (!date) {
    return 'Invalid Date';
  }

  const defaultOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  };

  return date.toLocaleDateString(locale, { ...defaultOptions, ...options });
};

/**
 * Day-first date, e.g. "Mon, 21 Dec 2026".
 * Pass `options` to drop parts, e.g. { weekday: undefined }.
 */
export const formatDateEG = (dateString, options = {}) =>
  formatDate(dateString, options, EG_LOCALE);

/** Day-first numeric date, e.g. "21/12/2026". Good for exports and tight columns. */
export const formatNumericDateEG = (dateString) => {
  const date = parseDate(dateString);
  if (!date) return 'Invalid Date';

  return date.toLocaleDateString(EG_LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

/** Day-first date and 24-hour time, e.g. "21 Dec 2026, 14:30". */
export const formatDateTimeEG = (dateString) => {
  const date = parseDate(dateString);
  if (!date) return 'Invalid Date';

  return date.toLocaleString(EG_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

/** Time only, 24-hour by default, e.g. "14:30". */
export const formatTimeEG = (dateString, options = {}) => {
  const date = parseDate(dateString);
  if (!date) return 'Invalid Date';

  return date.toLocaleTimeString(EG_LOCALE, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...options
  });
};

// Format date for short display (e.g., "Jan 15")
export const formatShortDate = (dateString) => {
  const date = parseDate(dateString);
  if (!date) return 'Invalid Date';
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

// Format date for full display (e.g., "Monday, January 15, 2024")
export const formatFullDate = (dateString) => {
  const date = parseDate(dateString);
  if (!date) return 'Invalid Date';
  
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Check if date is today
export const isToday = (dateString) => {
  const date = parseDate(dateString);
  if (!date) return false;
  
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

// Check if date is in the future
export const isFuture = (dateString) => {
  const date = parseDate(dateString);
  if (!date) return false;
  
  const today = new Date();
  return date > today;
};

// Get relative date string (e.g., "Today", "Tomorrow", "Yesterday")
export const getRelativeDate = (dateString) => {
  const date = parseDate(dateString);
  if (!date) return 'Invalid Date';
  
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  
  return formatShortDate(dateString);
}; 